import type { ThemeConfig } from 'antd';

/**
 * Rescue Foodies Consumer App Theme Configuration
 * 
 * Brand Colors (Client Inspiration Palette):
 * - Primary (Golden Orange): #F5A623 - CTAs, primary actions
 * - Success (Sage Green): #4A7C59 - Success states, sustainability
 * - Background (Warm Cream): #FDF8ED - Page backgrounds
 * 
 * Design System: docs/front-end-spec.md
 * Inspiration: docs/client/marketing/inspiration/
 */
export const consumerTheme: ThemeConfig = {
  token: {
    // === BRAND COLORS (Client Palette) ===
    colorPrimary: '#F5A623',        // Golden orange for CTAs (Reserve Bag, etc.)
    colorSuccess: '#4A7C59',        // Sage green for success states
    colorError: '#E53935',          // Warm danger red
    colorWarning: '#FFA726',        // Warning orange
    colorInfo: '#2196F3',           // Info blue

    // === TYPOGRAPHY ===
    fontFamily: 'Poppins, -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif',
    fontSize: 16,                   // Base font size
    fontSizeHeading1: 36,
    fontSizeHeading2: 30,
    fontSizeHeading3: 24,
    fontSizeHeading4: 20,
    fontWeightStrong: 600,
    lineHeight: 1.5,
    lineHeightHeading1: 1.25,
    lineHeightHeading2: 1.25,

    // === SPACING & SIZING ===
    borderRadius: 12,               // Default border radius
    borderRadiusLG: 16,             // Large cards
    borderRadiusSM: 8,              // Small elements
    controlHeight: 48,              // Button/input height (WCAG AA compliant)
    controlHeightLG: 56,            // Large buttons
    controlHeightSM: 40,            // Small buttons

    // === LAYOUT ===
    padding: 16,
    paddingLG: 24,
    paddingXL: 32,
    margin: 16,
    marginLG: 24,
    marginXL: 32,

    // === BORDERS (Sage-Tinted) ===
    lineWidth: 2,
    colorBorder: '#D4DDD0',         // Sage-tinted border
    colorBorderSecondary: '#E8F0E3', // Soft sage border

    // === BACKGROUNDS (Warm Cream System) ===
    colorBgContainer: '#FFFFFF',    // White for cards
    colorBgLayout: '#FDF8ED',       // Warm cream page background
    colorBgElevated: '#FFFFFF',
    colorFillQuaternary: '#F5F0E6', // Warm cream variant

    // === TEXT COLORS (Warm Green-Gray) ===
    colorText: '#2C3E2D',           // Primary text (dark green-gray)
    colorTextSecondary: '#5D6B5E',  // Secondary text (sage gray)
    colorTextTertiary: '#8A9A8B',   // Tertiary text (muted sage)
    colorTextQuaternary: '#B5C4B6', // Disabled text (light sage)

    // === ELEVATION (SHADOWS) ===
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.06), 0 4px 8px rgba(0, 0, 0, 0.04)',
    boxShadowSecondary: '0 4px 12px rgba(0, 0, 0, 0.08), 0 2px 4px rgba(0, 0, 0, 0.04)',

    // === MOTION ===
    motionDurationMid: '0.25s',
    motionDurationSlow: '0.35s',
    motionEaseInOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
  },

  components: {
    // === BUTTONS ===
    Button: {
      controlHeight: 48,
      controlHeightLG: 56,
      borderRadius: 12,
      fontWeight: 600,
      primaryShadow: '0 4px 16px rgba(245, 166, 35, 0.24)', // Golden orange glow
      defaultShadow: 'none', // No shadow on default buttons for flat header style
    },

    // === CARDS ===
    Card: {
      borderRadius: 16,
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.06), 0 4px 8px rgba(0, 0, 0, 0.04)',
      paddingLG: 24,
    },

    // === TAGS/BADGES ===
    Tag: {
      borderRadiusSM: 20, // Pill shape for category badges
      fontSize: 12,
      fontSizeSM: 11,
    },

    // === INPUTS ===
    Input: {
      controlHeight: 48,
      borderRadius: 12,
      paddingBlock: 12,
      paddingInline: 16,
      activeBorderColor: '#F5A623',
      hoverBorderColor: '#F5A623',
    },

    // === SELECT ===
    Select: {
      controlHeight: 48,
      borderRadius: 12,
    },

    // === LAYOUT ===
    Layout: {
      headerBg: '#FFFFFF',
      headerHeight: 72,
      footerBg: '#F5F0E6',  // Warm cream variant (aligned with marketing site)
      bodyBg: '#FDF8ED',    // Warm cream page background (aligned with marketing site)
    },

    // === MENU ===
    Menu: {
      itemBorderRadius: 8,
      itemHoverBg: '#FEF7E6', // Light golden on hover
      itemSelectedBg: '#FDEFC7', // Selected golden
      itemSelectedColor: '#E8960F', // Darker golden for contrast
    },
  },
};
